import {matchHash} from '@github-ui/diffs/document-hash-helpers'
import type {DiffAnchor, LineRange} from '@github-ui/diffs/types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useAnalytics} from '@github-ui/use-analytics'
import {type FocusEvent, type RefObject, useMemo, useRef} from 'react'

import type {ConfigureSuggestedChangesImplementation} from '../../conversation-types'
import type {StartThreadCommentProps} from './StartThreadComment'
import {StartThreadComment} from './StartThreadComment'

export interface StartConversationProps
  extends Pick<
    StartThreadCommentProps,
    | 'addCommentDialogTitle'
    | 'batchPending'
    | 'batchingEnabled'
    | 'fileLevelComment'
    | 'filePath'
    | 'isLeftSide'
    | 'lineNumber'
    | 'onAddComment'
    | 'startLineNumber'
    | 'subjectId'
    | 'viewerData'
    | 'threadsConnectionId'
  > {
  anchorRef: RefObject<HTMLElement>
  diffAnchor: DiffAnchor
  isInDialogMode: boolean
  isOpen: boolean
  onCloseCommentDialog: () => void
  returnFocusRef: RefObject<HTMLElement>
  suggestedChangesConfig?: ConfigureSuggestedChangesImplementation
}

/**
 * Renders a modal AnchoredOverlay for starting a new conversation thread or an in line version
 */
export function StartConversation({
  anchorRef,
  diffAnchor,
  isInDialogMode,
  isOpen,
  returnFocusRef,
  onCloseCommentDialog,
  suggestedChangesConfig,
  ...rest
}: StartConversationProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const newCommentContainerRef = useRef<HTMLDivElement>(null)

  const config = useMemo(() => {
    if (isOpen && suggestedChangesConfig?.configureSuggestedChangesFromLineRange) {
      // TODO: update this to use `useGetSelectedLineRange` hook once this file lives in `@github-ui/pull-requests`
      const selectedLineRange = getSelectedLineRangeFromUrl(diffAnchor)
      return suggestedChangesConfig.configureSuggestedChangesFromLineRange(
        selectedLineRange,
        suggestedChangesConfig?.shouldStartNewConversationWithSuggestedChange,
      )
    }
    return undefined
  }, [diffAnchor, isOpen, suggestedChangesConfig])

  if (!isOpen) return null

  const handleNewCommentContainerFocus = (e: FocusEvent) => {
    // Only handle focus logic if a user is on the new comment container's first <div> element.
    if (e.target !== newCommentContainerRef.current) return

    // Focus on the textarea element inside of the new comment container
    newCommentContainerRef.current.querySelector('textarea')?.focus()
  }

  return (
    <div
      className="rounded-2 bgColor-default"
      data-marker-navigation-new-thread
      data-marker-id="new-comment" // Add data-marker-id attribute for keyboard navigation
      ref={newCommentContainerRef}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isInDialogMode ? 0 : -1}
      onFocus={handleNewCommentContainerFocus}
    >
      <StartThreadComment
        onClose={() => {
          onCloseCommentDialog()
          sendAnalyticsEvent('comments.cancel_thread_reply', 'CANCEL_REVIEW_THREAD_BUTTON')
        }}
        showOnCloseIcon={false}
        suggestedChangesConfig={config}
        {...rest}
      />
    </div>
  )
}

// TODO: Remove this function once this package lives in `@github-ui/pull-requests`
function getSelectedLineRangeFromUrl(diffAnchor: DiffAnchor): LineRange | undefined {
  const match = matchHash(ssrSafeLocation.hash)

  if (!match) return

  // match[0] is the full match so start at 1
  const diffAnchorInHash = match[1]

  if (diffAnchorInHash !== diffAnchor) return

  // start of range
  const startOrientation = match[2] ? (match[2] === 'L' ? 'left' : 'right') : undefined
  const startLineNumber = match[3] ? parseInt(match[3]) : undefined
  // end of range
  const endOrientation = match[4] ? (match[4] === 'L' ? 'left' : 'right') : undefined
  const endLineNumber = match[5] ? parseInt(match[5]) : undefined

  if (!startOrientation || !startLineNumber) return

  return {
    diffAnchor,
    endOrientation: endOrientation ?? startOrientation,
    endLineNumber: endLineNumber ?? startLineNumber,
    startOrientation,
    startLineNumber,
  }
}

try{ StartConversation.displayName ||= 'StartConversation' } catch {}